import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { Alert } from 'antd';
import styled from 'styled-components';

import { getRecommendations } from 'apis/RecommendationsAPI';
import Button from 'components/lib/Button';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { WEBSITE_VERIFICATION_FAILURES_QUERY_KEY } from './constants';
import RecommendationEvidenceModal from './RecommendationEvidenceModal';
import useRecommendationsContext from './RecommendationsContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 30px;
`;

const WebsiteVerificationFailures = () => {
  const { company } = useRecommendationsContext();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);
  const [selectedRun, setSelectedRun] = useState<number | null>(null);
  const canViewEvidence = Boolean(currentUser?.isSuperuser || isManagedBySuperuser(currentUser));
  const { data = [], isError } = useQuery({
    queryKey: [WEBSITE_VERIFICATION_FAILURES_QUERY_KEY, company?.id, selectedCommunity?.id],
    queryFn: () =>
      getRecommendations(company?.id ?? -1, {
        verification_failed_community: selectedCommunity?.id ?? -1,
      }),
    enabled: canViewEvidence && Boolean(selectedCommunity?.id && company?.id),
    refetchOnWindowFocus: false,
  });

  if (!canViewEvidence || !selectedCommunity || (!data.length && !isError)) return null;
  return (
    <Container>
      {isError && <Alert type="warning" message="Failed to load website verification diagnostics." />}
      {data.map((run) => (
        <Alert
          key={run.id}
          type="warning"
          showIcon
          message="A Website Experience insight could not be verified"
          description={`Attempt from ${new Date(run.created_at).toLocaleString()}. Open the evidence to see the reason.`}
          action={
            <Button onClick={() => setSelectedRun(run.id)} data-test-id={`aici-failed-run-evidence-${run.id}`}>
              Website evidence
            </Button>
          }
        />
      ))}
      {selectedRun !== null && (
        <RecommendationEvidenceModal
          open
          recommendationId={selectedRun}
          communityId={selectedCommunity.id}
          communityName={selectedCommunity.name}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </Container>
  );
};

export default WebsiteVerificationFailures;
